/**
 * XR Pane Controls
 *
 * Touch controls for manipulating the reading pane in mobile 3D XR mode.
 * Controls work in camera-relative space for intuitive interaction:
 * - Single finger drag: rotate pane in place (no position drift)
 * - Two finger drag: pan pane in camera-relative X/Y
 * - Two finger pinch: scale pane (zoom)
 *
 * Camera is controlled by gyros, this only affects the pane.
 */

import * as THREE from 'three';

/**
 * Pane controls configuration
 */
interface PaneControlsConfig {
  /** Rotation sensitivity */
  rotateSpeed: number;
  /** Pan sensitivity */
  panSpeed: number;
  /** Scale sensitivity */
  scaleSpeed: number;
  /** Minimum scale */
  minScale: number;
  /** Maximum scale */
  maxScale: number;
}

const DEFAULT_CONFIG: PaneControlsConfig = {
  rotateSpeed: 0.005,
  panSpeed: 0.003,
  scaleSpeed: 0.005,
  minScale: 0.5,
  maxScale: 2.0,
};

/**
 * Touch controls for manipulating the reading pane and hints pane together
 */
export class XRPaneControls {
  private pane: THREE.Mesh;
  private hintsPane: THREE.Mesh | null;
  private camera: THREE.Camera | null;
  private domElement: HTMLElement;
  private config: PaneControlsConfig;

  // Relative offset of hints pane from reading pane (calculated on init)
  private hintsPaneOffset: THREE.Vector3 | null = null;

  // State tracking
  private isEnabled: boolean = false;
  private isTouchOnUI: boolean = false;
  private lastTouchX: number = 0;
  private lastTouchY: number = 0;
  private lastTouchDistance: number = 0;
  private currentScale: number = 1.0;

  // Bound event handlers
  private boundOnTouchStart: (e: TouchEvent) => void;
  private boundOnTouchMove: (e: TouchEvent) => void;
  private boundOnTouchEnd: (e: TouchEvent) => void;

  constructor(
    pane: THREE.Mesh,
    domElement: HTMLElement,
    config: Partial<PaneControlsConfig> = {},
    hintsPane: THREE.Mesh | null = null,
    camera: THREE.Camera | null = null
  ) {
    this.pane = pane;
    this.hintsPane = hintsPane;
    this.camera = camera;
    this.domElement = domElement;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Calculate initial offset of hints pane from reading pane
    if (hintsPane) {
      this.hintsPaneOffset = new THREE.Vector3().subVectors(
        hintsPane.position,
        pane.position
      );
    }

    // Bind event handlers
    this.boundOnTouchStart = this.onTouchStart.bind(this);
    this.boundOnTouchMove = this.onTouchMove.bind(this);
    this.boundOnTouchEnd = this.onTouchEnd.bind(this);
  }

  /**
   * Set camera reference (needed for camera-relative controls)
   */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  /**
   * Enable pane controls
   */
  enable(): void {
    if (this.isEnabled) return;

    this.domElement.addEventListener('touchstart', this.boundOnTouchStart, { passive: false });
    window.addEventListener('touchmove', this.boundOnTouchMove, { passive: false });
    window.addEventListener('touchend', this.boundOnTouchEnd);

    this.isEnabled = true;
    console.log('[XR] Pane controls enabled');
  }

  /**
   * Disable pane controls
   */
  disable(): void {
    if (!this.isEnabled) return;

    this.domElement.removeEventListener('touchstart', this.boundOnTouchStart);
    window.removeEventListener('touchmove', this.boundOnTouchMove);
    window.removeEventListener('touchend', this.boundOnTouchEnd);

    this.isEnabled = false;
    console.log('[XR] Pane controls disabled');
  }

  /**
   * Check if element is a UI control
   */
  private isUIElement(element: Element | null): boolean {
    if (!element) return false;

    let current: Element | null = element;
    while (current) {
      if (
        current.classList?.contains('mobile-controls') ||
        current.classList?.contains('mobile-xr-buttons') ||
        current.classList?.contains('mobile-camera-toggle') ||
        current.classList?.contains('wpm-slider') ||
        current.classList?.contains('transport-btn') ||
        current.classList?.contains('control-btn') ||
        current.tagName === 'INPUT' ||
        current.tagName === 'BUTTON' ||
        current.tagName === 'LABEL'
      ) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  /**
   * Handle touch start
   */
  private onTouchStart(event: TouchEvent): void {
    if (this.isUIElement(event.target as Element)) {
      this.isTouchOnUI = true;
      return;
    }

    this.isTouchOnUI = false;
    event.preventDefault();

    if (event.touches.length === 1) {
      // Single touch: track for rotation
      this.lastTouchX = event.touches[0].clientX;
      this.lastTouchY = event.touches[0].clientY;
    } else if (event.touches.length === 2) {
      // Two touches: track for pan/pinch
      this.lastTouchDistance = this.getTouchDistance(event.touches);
      const center = this.getTouchCenter(event.touches);
      this.lastTouchX = center.x;
      this.lastTouchY = center.y;
    }
  }

  /**
   * Handle touch move
   */
  private onTouchMove(event: TouchEvent): void {
    if (this.isTouchOnUI) return;
    if (this.isUIElement(event.target as Element)) return;
    if (!this.isEnabled) return;

    event.preventDefault();

    if (event.touches.length === 1) {
      // Single finger: rotate pane in place (no position change)
      // Use world Y for horizontal (prevents drift) and camera right for vertical
      const deltaX = event.touches[0].clientX - this.lastTouchX;
      const deltaY = event.touches[0].clientY - this.lastTouchY;

      // Horizontal rotation around world Y axis (always vertical, no drift)
      const worldUp = new THREE.Vector3(0, 1, 0);
      const rotHorizontal = new THREE.Quaternion().setFromAxisAngle(
        worldUp,
        deltaX * this.config.rotateSpeed
      );

      // Vertical rotation around camera's right axis (for intuitive up/down)
      if (this.camera) {
        const cameraRight = new THREE.Vector3();
        this.camera.matrixWorld.extractBasis(cameraRight, new THREE.Vector3(), new THREE.Vector3());

        const rotVertical = new THREE.Quaternion().setFromAxisAngle(
          cameraRight,
          deltaY * this.config.rotateSpeed
        );

        // Apply rotations separately to avoid interaction
        this.pane.quaternion.premultiply(rotHorizontal);
        this.pane.quaternion.premultiply(rotVertical);
      } else {
        this.pane.quaternion.premultiply(rotHorizontal);
      }

      this.lastTouchX = event.touches[0].clientX;
      this.lastTouchY = event.touches[0].clientY;

      // Sync hints pane
      this.syncHintsPane();
    } else if (event.touches.length === 2) {
      // Two fingers: pan + pinch
      const distance = this.getTouchDistance(event.touches);
      const center = this.getTouchCenter(event.touches);

      // Pinch: scale pane
      const deltaDistance = distance - this.lastTouchDistance;
      this.currentScale += deltaDistance * this.config.scaleSpeed;
      this.currentScale = Math.max(this.config.minScale, Math.min(this.config.maxScale, this.currentScale));
      this.pane.scale.setScalar(this.currentScale);

      // Two-finger drag: pan pane in camera-relative space
      const deltaX = center.x - this.lastTouchX;
      const deltaY = center.y - this.lastTouchY;

      if (this.camera) {
        // Get camera's right and up vectors for camera-relative panning
        const right = new THREE.Vector3();
        const up = new THREE.Vector3();
        this.camera.matrix.extractBasis(right, up, new THREE.Vector3());

        // Pan in camera space
        this.pane.position.addScaledVector(right, deltaX * this.config.panSpeed);
        this.pane.position.addScaledVector(up, -deltaY * this.config.panSpeed);
      } else {
        // Fallback: pan in world X/Y
        this.pane.position.x += deltaX * this.config.panSpeed;
        this.pane.position.y -= deltaY * this.config.panSpeed;
      }

      this.lastTouchDistance = distance;
      this.lastTouchX = center.x;
      this.lastTouchY = center.y;

      // Sync hints pane
      this.syncHintsPane();
    }
  }

  /**
   * Sync hints pane position/scale/rotation with reading pane
   */
  private syncHintsPane(): void {
    if (!this.hintsPane || !this.hintsPaneOffset) return;

    // Scale the offset by current scale to maintain relative positioning
    // This prevents overlap when the reading pane is enlarged
    const scaledOffset = this.hintsPaneOffset.clone().multiplyScalar(this.currentScale);

    // Keep hints pane at scaled offset from reading pane
    this.hintsPane.position.copy(this.pane.position).add(scaledOffset);

    // Match scale and rotation
    this.hintsPane.scale.copy(this.pane.scale);
    this.hintsPane.rotation.copy(this.pane.rotation);
  }

  /**
   * Handle touch end
   */
  private onTouchEnd(_event: TouchEvent): void {
    this.isTouchOnUI = false;
  }

  /**
   * Get distance between two touches
   */
  private getTouchDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Get center point between two touches
   */
  private getTouchCenter(touches: TouchList): { x: number; y: number } {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  /**
   * Reset pane to default position/scale
   */
  reset(position: THREE.Vector3, rotation: number = 0): void {
    this.pane.position.copy(position);
    this.pane.rotation.y = rotation;
    this.currentScale = 1.0;
    this.pane.scale.setScalar(1.0);
  }
}

/**
 * Create pane controls
 */
export function createXRPaneControls(
  pane: THREE.Mesh,
  domElement: HTMLElement,
  config?: Partial<PaneControlsConfig>,
  hintsPane?: THREE.Mesh | null,
  camera?: THREE.Camera | null
): XRPaneControls {
  return new XRPaneControls(pane, domElement, config, hintsPane ?? null, camera ?? null);
}
